import {
  ClampToEdgeWrapping,
  DataTexture,
  DoubleSide,
  FloatType,
  LinearFilter,
  Mesh,
  PlaneBufferGeometry,
  RedFormat,
  ShaderMaterial,
  UVMapping,
} from "three";
import vertexShader from "raw-loader!glslify-loader!./shaders/CountingContourVert.glsl";
import fragmentShader from "raw-loader!glslify-loader!./shaders/CountingContourFrag.glsl";

function amin(a, map = (x) => x) {
  return a.reduce((a, b) => Math.min(a, map(b)), +Infinity);
}

function amax(a, map = (x) => x) {
  return a.reduce((a, b) => Math.max(a, map(b)), -Infinity);
}

const material = new ShaderMaterial({
  uniforms: {
    data: { value: null },
    borderWidth: { value: 0.08 },
    heightScale: { value: [1, 1] },
    offset: { value: 0 },
    steps: { value: 2 },
    lerp: { value: 0 },
    xBounds: { value: [-1, 1] },
    yBounds: { value: [-1, 1] },
    ymax: { value: 0 },
  },
  side: DoubleSide,
  vertexShader,
  fragmentShader,
});

export default class CountingContour extends Mesh {
  constructor(data) {
    super(
      new PlaneBufferGeometry(1, 1, data.x.length, data.y.length),
      material.clone()
    );

    this.frustumCulled = false;

    this.xBounds = [amin(data.x), amax(data.x)];
    this.yBounds = [amin(data.y), amax(data.y)];
    this.data = data;

    const fs = [];
    this.heightScales = [];
    let i = 0;
    for (const f of this.data.f) {
      const x = Math.floor(data.x.length / 2) - 3;
      const y =
        Math.floor(
          ((data.ymax[i] - this.yBounds[0]) /
            (this.yBounds[1] - this.yBounds[0])) *
            data.y.length
        ) - 5;
      this.heightScales.push(
        Math.sign(f[x * data.y.length + y]) / amax(f, Math.abs)
      );

      fs.push(...f);

      ++i;
    }

    const u = this.material.uniforms;
    u.data.value = new DataTexture(
      new Float32Array(fs),
      data.y.length,
      data.x.length * data.f.length,
      RedFormat,
      FloatType,
      UVMapping,
      ClampToEdgeWrapping,
      ClampToEdgeWrapping,
      LinearFilter,
      LinearFilter
    );
    u.xBounds.value = this.xBounds;
    u.yBounds.value = this.yBounds;
    u.steps.value = data.f.length;
    this.ymax = this.yBounds[1];
  }

  set ymax(v) {
    const ymax = this.data.ymax;
    let i = 0;
    while (v > ymax[i]) ++i;

    const u = this.material.uniforms;
    u.offset.value = i - 1;
    const lerp = (v - ymax[i - 1]) / (ymax[i] - ymax[i - 1]);
    u.lerp.value = lerp;
    u.ymax.value = v;
    u.heightScale.value = [this.heightScales[i - 1], this.heightScales[i]];
  }

  dispose() {
    this.geometry.dispose();
    this.material.uniforms.data.value.dispose();
    this.material.dispose();
  }
}
